import React from 'react';
import styled from 'styled-components';
import { FiEdit2, FiTarget, FiSend, FiFlag, FiClock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${theme.colors.primary[300]};
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  flex: 1;
`;

const FunilNome = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.fontSizes.base};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const EtapaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const StatusBadge = styled.span`
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  background: ${props => props.$ativo ? theme.colors.success[500] : theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  font-weight: 700;
  font-size: ${theme.fontSizes.xs};
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  align-items: center;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  border: 1px solid ${theme.colors.border.primary};
`;

const MensagemTexto = styled.p`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.primary[500]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary[600]};
    border-color: ${theme.colors.primary[600]};
  }
`;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const MensagemEtapaCard = ({ mensagem }) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    if (!mensagem?.id) return;
    if (import.meta.env.DEV) {
      console.debug('[MensagemEtapaCard] Abrindo edição', { mensagemId: mensagem.id });
    }
    navigate(`/leads/mensagens-etapa/${mensagem.id}/editar`);
  };

  return (
    <CardContainer>
      <Header>
        <TitleGroup>
          <FunilNome>
            <FiTarget />
            {mensagem.funil_nome || 'Funil não informado'}
          </FunilNome>
          <EtapaInfo>
            <FiFlag />
            {mensagem.etapa_ordem ? `Etapa ${mensagem.etapa_ordem} - ` : ''}{mensagem.etapa_nome || 'Etapa'}
          </EtapaInfo>
        </TitleGroup>
        <StatusBadge $ativo={mensagem.ativa}>
          {mensagem.ativa ? 'Ativa' : 'Inativa'}
        </StatusBadge>
      </Header>

      <Body>
        <BadgeRow>
          <Chip>
            <FiSend />
            {mensagem.canal || 'Canal não informado'}
          </Chip>
          <Chip>
            <FiClock />
            Criada em {formatDate(mensagem.created_at)}
          </Chip>
        </BadgeRow>

        <MensagemTexto title={mensagem.mensagem}>
          {mensagem.mensagem}
        </MensagemTexto>
      </Body>

      <Actions>
        <ActionButton type="button" onClick={handleEdit}>
          <FiEdit2 />
          Editar
        </ActionButton>
      </Actions>
    </CardContainer>
  );
};

export default MensagemEtapaCard;

